import { Transition } from '@headlessui/react'

import { Form } from '@tribeplatform/react-components/Form'
import { T, useI18n } from '@tribeplatform/react-components/i18n'
import { useNetwork, useUpdateNetwork } from '@tribeplatform/react-sdk/hooks'
import { Link } from '@tribeplatform/react-ui-kit/Link'
import { toast } from '@tribeplatform/react-ui-kit/Toast'

import { SettingsLayout } from '../Layout/SettingsLayout.js'

export const CaptchaSettings = () => {
  const { $t } = useI18n()
  const { data: network } = useNetwork()
  const { mutateAsync: updateNetwork, isLoading } = useUpdateNetwork()

  return (
    <Form
      defaultValues={{
        settings: {
          captcha: {
            enabled: network?.settings?.captcha?.enabled,
            siteKey: network?.settings?.captcha?.siteKey,
            secretKey: network?.settings?.captcha?.secretKey,
          },
        },
      }}
      onSubmit={data => {
        updateNetwork({ input: data })
          .then(() => {
            toast({
              title: $t({
                defaultMessage: 'Google reCAPTCHA settings updated.',
                id: 'Admin.Security.Captcha.Updated',
              }),
              status: 'success',
            })
          })
          .catch(() => {
            toast({
              title: $t({
                defaultMessage: 'Unable to update Google reCAPTCHA settings',
                id: 'Admin.Security.Captcha.FailedToUpdate',
              }),
              status: 'error',
            })
          })
      }}
    >
      {({ watch, formState: { isDirty }, reset }) => (
        <SettingsLayout
          title={$t({
            defaultMessage: 'Google reCAPTCHA v3',
            id: 'Admin.Security.Captcha',
          })}
          subtitle={
            <>
              <T
                defaultMessage="Add an extra layer of security to your community. It verifies that users are genuine and not bots during the signup process, ensuring a safer environment."
                id="Admin.Security.Captcha.Description"
              />{' '}
              <Link
                href="https://www.google.com/recaptcha/intro/v3.html?ref=techmoon"
                target="_blank"
                rel="noopener noreferrer"
              >
                <T defaultMessage="Learn more" id="Generics.LearnMore" />
              </Link>
            </>
          }
          disabled={!isDirty}
          isLoading={isLoading}
          onCancel={() => reset()}
          saveLabel={<T defaultMessage="Update" id="Generics.Update" />}
        >
          <div className="web:col-span-12 web:md:col-span-8 flex flex-col space-y-5">
            <Form.Toggle
              name="settings.captcha.enabled"
              label={$t({
                defaultMessage: 'Enable invisible reCAPTCHA for signup',
                id: 'Admin.Security.Captcha.Enabled',
              })}
              helperText={
                <>
                  <T
                    defaultMessage="Register your domain with the Google reCAPTCHA V3 service and enter the provided keys below."
                    id="Admin.Security.Captcha.Enabled.Description"
                  />{' '}
                  <Link
                    href="https://developers.google.com/recaptcha/docs/v3"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <T
                      defaultMessage="Get the API keys"
                      id="Admin.Captcha.Enabled.Link"
                    />
                  </Link>
                </>
              }
            />
            <Transition
              as="div"
              className="py-4 mt-4 space-y-5"
              show={watch('settings.captcha.enabled') === true}
              enter="transition-all duration-100"
              enterFrom="opacity-0"
              enterTo="opacity-100"
              leave="transition-all duration-200"
              leaveFrom="opacity-100"
              leaveTo="opacity-0"
            >
              <Form.InputCopy
                name="settings.captcha.siteKey"
                label={$t({
                  defaultMessage: 'Site key',
                  id: 'Admin.Captcha.SiteKey',
                })}
                controlLabels={{
                  show: $t({
                    defaultMessage: 'Show',
                    id: 'Generics.Show.Verb',
                  }),
                  hide: $t({
                    defaultMessage: 'Hide',
                    id: 'Generics.Hide.Verb',
                  }),
                  copy: $t({
                    defaultMessage: 'Copy',
                    id: 'Generics.Copy.Verb',
                  }),
                  copied: $t({
                    defaultMessage: 'Copied',
                    id: 'Generics.Copied',
                  }),
                }}
                validation={{
                  required:
                    watch('settings.captcha.enabled') === true
                      ? $t({
                          defaultMessage: 'Please enter the provided site key',
                          id: 'Admin.Captcha.SiteKey.Validation.Required',
                        })
                      : false,
                }}
                hideValue
              />
              <Form.InputCopy
                name="settings.captcha.secretKey"
                label={$t({
                  defaultMessage: 'Secret key',
                  id: 'Admin.Captcha.SecretKey',
                })}
                controlLabels={{
                  show: $t({
                    defaultMessage: 'Show',
                    id: 'Generics.Show.Verb',
                  }),
                  hide: $t({
                    defaultMessage: 'Hide',
                    id: 'Generics.Hide.Verb',
                  }),
                  copy: $t({
                    defaultMessage: 'Copy',
                    id: 'Generics.Copy.Verb',
                  }),
                  copied: $t({
                    defaultMessage: 'Copied',
                    id: 'Generics.Copied',
                  }),
                }}
                validation={{
                  required:
                    watch('settings.captcha.enabled') === true
                      ? $t({
                          defaultMessage:
                            'Please enter the provided secret key',
                          id: 'Admin.Captcha.SecretKey.Validation.Required',
                        })
                      : false,
                }}
                hideValue
              />
            </Transition>
          </div>
        </SettingsLayout>
      )}
    </Form>
  )
}
